import type { FieldValues, Path, UseFormReturn } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { ApiError, ApiTimeoutError } from '@/lib/api/client.js';

/**
 * Translates a thrown API error into a message for the user.
 *
 * The server's stable `code` is what gets translated; its English `message` is
 * never shown, so each product locale owns its user-facing copy.
 */
export function useApiError() {
  const { t } = useTranslation('errors');

  return (error: unknown): { message: string } => {
    // Rule E8: the request may already have succeeded server-side - this is
    // never worded like a confirmed failure, and never invites a blind retry
    // of a consequential mutation.
    if (error instanceof ApiTimeoutError) return { message: t('timeout') };

    if (error instanceof ApiError) {
      return { message: t(error.code, { defaultValue: t('INTERNAL_ERROR') }) };
    }

    return { message: t('network') };
  };
}

interface ApiFieldError {
  path?: unknown;
  message?: unknown;
}

/**
 * Maps `error.fieldErrors` (validation) onto the matching react-hook-form
 * field, so a drawer form shows the problem next to the cause instead of only
 * a generic toast (ui-contract.md section 6). Ported from the Unified Access
 * web kit's `useApplyFieldErrors`. Returns whether it applied anything, so
 * the caller can fall back to a toast otherwise.
 */
export function useApplyFieldErrors() {
  const { t } = useTranslation('errors');

  return <T extends FieldValues>(form: UseFormReturn<T>, error: unknown): boolean => {
    if (!(error instanceof ApiError)) return false;
    const fieldErrors = error.details['fieldErrors'];
    if (!Array.isArray(fieldErrors)) return false;

    const message = t(error.code, { defaultValue: t('INTERNAL_ERROR') });
    let applied = false;
    for (const entry of fieldErrors as ApiFieldError[]) {
      const path = entry?.path;
      if (typeof path !== 'string' || path.length === 0) continue;
      form.setError(path as Path<T>, { type: 'server', message });
      applied = true;
    }
    return applied;
  };
}
